import type { FeatureName } from 'constant/features';

export const FEATURE_NOT_ENABLED_MSG = 'Feature not enabled';

/** Missing / null / omitted / non-array values are treated as no entitlements. */
export function normalizeEnabledFeatures(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
}

export function hasFeature(
    enabledFeatures: readonly string[] | null | undefined,
    name: FeatureName | string | null | undefined
): boolean {
    if (!name || !Array.isArray(enabledFeatures)) {
        return false;
    }

    return enabledFeatures.includes(name);
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

const readEnabledFeaturesField = (payload: unknown): { present: boolean; value: unknown } => {
    if (!isRecord(payload)) {
        return { present: false, value: undefined };
    }

    if ('enabled_features' in payload) {
        return { present: true, value: payload.enabled_features };
    }

    if (isRecord(payload.data) && 'enabled_features' in payload.data) {
        return { present: true, value: payload.data.enabled_features };
    }

    return { present: false, value: undefined };
};

/** True when the payload includes `enabled_features` (even if null / []). */
export function payloadHasEnabledFeatures(payload: unknown): boolean {
    return readEnabledFeaturesField(payload).present;
}

/** Reads `enabled_features` from a current-user / auth payload. Omitted → []. */
export function pickEnabledFeatures(payload: unknown): string[] {
    const { present, value } = readEnabledFeaturesField(payload);
    if (!present) {
        return [];
    }
    return normalizeEnabledFeatures(value);
}

const collectErrorPayloads = (error: unknown): unknown[] => {
    if (error == null) {
        return [];
    }

    if (typeof error === 'string') {
        return [error];
    }

    if (!isRecord(error)) {
        return [];
    }

    const payloads: unknown[] = [];

    if ('response' in error) {
        payloads.push((error as { response?: { data?: unknown } }).response?.data);
    }
    if ('data' in error) {
        payloads.push(error.data);
    }
    payloads.push(error);

    return payloads;
};

const readLockedFeatureFromPayload = (payload: unknown): string | null => {
    if (typeof payload === 'string' && payload.trim() === FEATURE_NOT_ENABLED_MSG) {
        return '';
    }

    if (!isRecord(payload)) {
        return null;
    }

    const msg = payload.msg;
    const feature = payload.feature;
    if (typeof msg === 'string' && msg.trim() === FEATURE_NOT_ENABLED_MSG) {
        return typeof feature === 'string' && feature.length > 0 ? feature : '';
    }

    return null;
};

/** Feature name from a 403 `{ msg: "Feature not enabled", feature: "..." }`, or null. */
export function getLockedFeatureName(error: unknown): string | null {
    for (const payload of collectErrorPayloads(error)) {
        const feature = readLockedFeatureFromPayload(payload);
        if (feature) {
            return feature;
        }
    }

    return null;
}

export function isFeatureNotEnabledError(error: unknown): boolean {
    for (const payload of collectErrorPayloads(error)) {
        if (readLockedFeatureFromPayload(payload) != null) {
            return true;
        }
    }

    return false;
}
